<template>
  <div class="payment-admins-management">
    <div class="admins-header">
      <h3>Администраторы платежа</h3>
      <a-button 
        type="primary" 
        @click="showAddAdminModal"
        :disabled="!canAddAdmins"
        size="small"
      >
        <template #icon>
          <PlusOutlined />
        </template>
        Добавить администратора
      </a-button>
    </div>

    <div v-if="loading" class="loading-container">
      <a-spin size="large" />
      <p>Загрузка администраторов...</p>
    </div>

    <div v-else-if="admins.length === 0" class="no-admins">
      <a-empty 
        description="Администраторы не назначены"
        :image="Empty.PRESENTED_IMAGE_SIMPLE"
      />
    </div>

    <div v-else class="admins-list">
      <div v-for="admin in admins" :key="admin.id" class="admin-card">
        <div class="admin-info">
          <a-avatar 
            :src="admin.photo_url" 
            :size="40"
            class="admin-avatar"
          >
            {{ admin.last_name?.[0] }}{{ admin.first_name?.[0] }}
          </a-avatar>
          <div class="admin-details">
            <div class="admin-name">
              {{ admin.last_name }} {{ admin.first_name }}
              <span v-if="admin.username" class="admin-username">@{{ admin.username }}</span>
            </div>
            <div class="admin-meta">
              <span class="admin-number">№{{ admin.number }}</span>
              <span class="admin-position">{{ admin.position }}</span>
            </div>
            <div class="admin-added-by">
              Добавлен: {{ admin.created_by_last_name }} {{ admin.created_by_first_name }}
            </div>
          </div>
        </div>
        <div class="admin-actions">
          <a-button 
            type="text" 
            danger 
            @click="removeAdmin(admin)"
            :disabled="!canRemoveAdmins"
            size="small"
          >
            <template #icon>
              <DeleteOutlined />
            </template>
            Удалить
          </a-button>
        </div>
      </div>
    </div>

    <!-- Modal для добавления администратора -->
    <a-modal
      v-model:open="addAdminModalVisible"
      title="Добавить администратора платежа"
      @ok="handleAddAdmin"
      @cancel="cancelAddAdmin"
      :confirm-loading="addingAdmin"
    >
      <div class="add-admin-form">
        <a-form-item label="Выберите игрока">
          <a-select
            v-model:value="selectedPlayerId"
            placeholder="Выберите игрока"
            :loading="loadingPlayers"
            show-search
            :filter-option="filterPlayerOption"
          >
            <a-select-option 
              v-for="player in availablePlayers" 
              :key="player.id" 
              :value="player.id"
            >
              <div class="player-option">
                <a-avatar :src="player.photo_url" :size="24">
                  {{ player.last_name?.[0] }}{{ player.first_name?.[0] }}
                </a-avatar>
                <span class="player-name">
                  {{ player.last_name }} {{ player.first_name }}
                  <span v-if="player.username">@{{ player.username }}</span>
                </span>
                <span class="player-number">№{{ player.number }}</span>
              </div>
            </a-select-option>
          </a-select>
        </a-form-item>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { message, Button, Spin, Empty, Modal, FormItem, Select, SelectOption, Avatar } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { api } from '../services/api.js';

export default {
  name: 'PaymentAdminsManagement',
  components: {
    PlusOutlined,
    DeleteOutlined,
    'a-button': Button,
    'a-spin': Spin,
    'a-empty': Empty,
    'a-modal': Modal,
    'a-form-item': FormItem,
    'a-select': Select,
    'a-select-option': SelectOption,
    'a-avatar': Avatar
  },
  props: {
    teamId: {
      type: Number,
      required: true
    },
    paymentTypeId: {
      type: Number,
      required: true
    },
    paymentTypeName: {
      type: String,
      required: true
    }
  },
  emits: ['admin-added', 'admin-removed'],
  setup(props, { emit }) {
    const admins = ref([]);
    const loading = ref(false);
    const addingAdmin = ref(false);
    const addAdminModalVisible = ref(false);
    const selectedPlayerId = ref(null);
    const availablePlayers = ref([]);
    const loadingPlayers = ref(false);

    // Проверка прав доступа
    const canAddAdmins = computed(() => {
      // Только администраторы команды могут добавлять администраторов платежей
      return true; // TODO: Добавить проверку прав
    });

    const canRemoveAdmins = computed(() => {
      // Только администраторы команды могут удалять администраторов платежей
      return true; // TODO: Добавить проверку прав
    });

    // Загрузка администраторов платежа
    const loadAdmins = async () => {
      try {
        loading.value = true;
        console.log('Loading admins for teamId:', props.teamId, 'paymentTypeId:', props.paymentTypeId);
        const response = await api.getPaymentAdmins(props.teamId, props.paymentTypeId);
        console.log('API response:', response);
        admins.value = Array.isArray(response) ? response : [];
        console.log('Admins loaded:', admins.value);
      } catch (error) {
        console.error('Error loading payment admins:', error);
        message.error('Ошибка при загрузке администраторов платежа');
        admins.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Загрузка доступных игроков
    const loadAvailablePlayers = async () => {
      try {
        loadingPlayers.value = true;
        const response = await api.getTeamPlayers(props.teamId);
        
        // Извлекаем всех игроков из объекта, сгруппированного по позициям
        const playersByPosition = response?.players || {};
        const allPlayers = [];
        
        // Объединяем игроков из всех позиций в один массив
        Object.values(playersByPosition).forEach(positionPlayers => {
          if (Array.isArray(positionPlayers)) {
            allPlayers.push(...positionPlayers);
          }
        });
        
        // Исключаем уже назначенных администраторов
        const adminPlayerIds = admins.value.map(admin => admin.player_id);
        availablePlayers.value = allPlayers.filter(player => 
          !adminPlayerIds.includes(player.id)
        );
      } catch (error) {
        console.error('Error loading available players:', error);
        message.error('Ошибка при загрузке игроков');
        availablePlayers.value = [];
      } finally {
        loadingPlayers.value = false;
      }
    };

    // Показать модальное окно добавления администратора
    const showAddAdminModal = async () => {
      await loadAvailablePlayers();
      addAdminModalVisible.value = true;
    };

    // Отменить добавление администратора
    const cancelAddAdmin = () => {
      addAdminModalVisible.value = false;
      selectedPlayerId.value = null;
    };

    // Добавить администратора
    const handleAddAdmin = async () => {
      if (!selectedPlayerId.value) {
        message.error('Выберите игрока');
        return;
      }

      try {
        addingAdmin.value = true;
        await api.addPaymentAdmin(props.teamId, props.paymentTypeId, selectedPlayerId.value);
        message.success('Администратор платежа добавлен');
        await loadAdmins();
        emit('admin-added', selectedPlayerId.value);
        cancelAddAdmin();
      } catch (error) {
        console.error('Error adding payment admin:', error);
        let errorMessage = 'Ошибка при добавлении администратора';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        message.error(errorMessage);
      } finally {
        addingAdmin.value = false;
      }
    };

    // Удалить администратора
    const removeAdmin = async (admin) => {
      try {
        await api.removePaymentAdmin(props.teamId, props.paymentTypeId, admin.id);
        message.success('Администратор платежа удален');
        await loadAdmins();
        emit('admin-removed', admin.id);
      } catch (error) {
        console.error('Error removing payment admin:', error);
        let errorMessage = 'Ошибка при удалении администратора';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        message.error(errorMessage);
      }
    };

    // Фильтр для поиска игроков
    const filterPlayerOption = (input, option) => {
      const player = option.children?.props?.children;
      if (!player) return false;
      
      const searchText = input.toLowerCase();
      const playerName = `${player.last_name} ${player.first_name}`.toLowerCase();
      const username = player.username?.toLowerCase() || '';
      const number = player.number?.toString() || '';
      
      return playerName.includes(searchText) || 
             username.includes(searchText) || 
             number.includes(searchText);
    };

    onMounted(() => {
      loadAdmins();
    });

    // Отслеживание изменений paymentTypeId
    watch(() => props.paymentTypeId, (newPaymentTypeId, oldPaymentTypeId) => {
      if (newPaymentTypeId !== oldPaymentTypeId) {
        console.log('PaymentTypeId changed from', oldPaymentTypeId, 'to', newPaymentTypeId);
        loadAdmins();
      }
    });

    return {
      admins,
      loading,
      addingAdmin,
      addAdminModalVisible,
      selectedPlayerId,
      availablePlayers,
      loadingPlayers,
      canAddAdmins,
      canRemoveAdmins,
      loadAdmins,
      showAddAdminModal,
      cancelAddAdmin,
      handleAddAdmin,
      removeAdmin,
      filterPlayerOption,
      Empty
    };
  }
};
</script>

<style scoped>
.payment-admins-management {
  padding: 16px;
}

.admins-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.admins-header h3 {
  margin: 0;
  color: #262626;
}

.loading-container {
  text-align: center;
  padding: 40px 0;
}

.no-admins {
  padding: 40px 0;
}

.admins-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.admin-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  transition: all 0.2s ease;
}

.admin-card:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.admin-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.admin-avatar {
  flex-shrink: 0;
}

.admin-details {
  flex: 1;
  min-width: 0;
}

.admin-name {
  font-weight: 500;
  color: #262626;
  margin-bottom: 4px;
}

.admin-username {
  color: #8c8c8c;
  font-size: 12px;
  margin-left: 8px;
}

.admin-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.admin-added-by {
  font-size: 11px;
  color: #bfbfbf;
}

.admin-actions {
  flex-shrink: 0;
}

.add-admin-form {
  padding: 16px 0;
}

.player-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.player-name {
  flex: 1;
  min-width: 0;
}

.player-number {
  color: #8c8c8c;
  font-size: 12px;
}

/* Responsive design */
@media (max-width: 768px) {
  .admins-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .admin-card {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .admin-info {
    justify-content: center;
  }

  .admin-actions {
    align-self: center;
  }
}
</style>
